import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles, ArrowRight, Play } from "lucide-react";
import { DoxMascot } from "@/components/Logo";
import { useTrialModal } from "@/context/TrialModalContext";
import { useDoxPreview } from "@/context/DoxPreviewContext";
import DoxVerifiedLine from "@/components/DoxVerifiedLine";
import api from "@/lib/api";

const SESSION_KEY = "dox-widget-state-v1";
const GREET_KEY = "dox-widget-greeted-v1";

// Ordered scripted onboarding — captures qualification data, then hands off to Claude.
const SCRIPT = [
  {
    key: "greeting",
    from: "dox",
    text: "Hi, I'm Dox — your AI Compliance Employee. I keep contractors licensed, insured, and on time with every renewal. Mind if I ask two quick questions so I can help right away?",
  },
  {
    key: "business_type",
    ask: "First — what kind of business do you run?",
    quickReplies: ["General Contractor", "Electrician", "Plumber", "HVAC", "Other"],
    field: "business_type",
  },
  {
    key: "state",
    ask: "Got it. And which state are you licensed in?",
    quickReplies: ["California", "Texas", "Florida", "New York", "Other"],
    field: "state",
  },
  {
    key: "pain",
    ask: "One last one. What's your biggest paperwork headache right now?",
    quickReplies: ["Renewals", "Insurance certificates", "Deadlines", "Filing paperwork"],
    field: "pain",
  },
  {
    key: "handoff",
    from: "dox",
    text: ({ business_type, state, pain }) =>
      `Perfect. I handle ${(
        pain || "renewals"
      ).toLowerCase()} for ${business_type ? business_type + "s" : "contractors"}${
        state ? " in " + state : ""
      } every single day. Ask me anything, or let me start working for you right now.`,
    cta: "ready", // shows Trial CTA card
  },
];

const loadState = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null") || {};
  } catch {
    return {};
  }
};

const saveState = (state) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch {
    /* no-op */
  }
};

/**
 * DoxWidget — persistent floating bottom-right chat assistant on every page.
 * • First-time visitors see an auto-greeting bubble (once per session).
 * • Scripted 3-question qualification (business type, state, pain point).
 * • After qualification, transitions to real Claude via /api/dox/public-chat.
 * • Every path surfaces a "Start Your 14-Day Free Trial" CTA that opens the
 *   TrialModal overlay — visitor never leaves the page they were reading.
 */
export default function DoxWidget() {
  const { openTrial } = useTrialModal();
  const { openDoxPreview, available: previewAvailable } = useDoxPreview();
  const initial = loadState();
  const [open, setOpen] = useState(false);
  const [greeted, setGreeted] = useState(() => sessionStorage.getItem(GREET_KEY) === "1");
  const [showGreetBubble, setShowGreetBubble] = useState(false);
  const [messages, setMessages] = useState(
    initial.messages || [{ from: "dox", text: SCRIPT[0].text }]
  );
  const [stepIndex, setStepIndex] = useState(initial.stepIndex ?? 1); // next scripted step
  const [answers, setAnswers] = useState(initial.answers || {});
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showCTA, setShowCTA] = useState(initial.showCTA || false);
  const scrollRef = useRef(null);

  // Auto-greeting bubble on first arrival (after 6s), once per session.
  useEffect(() => {
    if (greeted) return;
    const t = setTimeout(() => setShowGreetBubble(true), 6000);
    return () => clearTimeout(t);
  }, [greeted]);

  useEffect(() => {
    if (open) {
      setShowGreetBubble(false);
      setGreeted(true);
      sessionStorage.setItem(GREET_KEY, "1");
    }
  }, [open]);

  useEffect(() => {
    saveState({ messages, stepIndex, answers, showCTA });
  }, [messages, stepIndex, answers, showCTA]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open, sending]);

  // When entering a new script step, auto-post Dox's next question.
  useEffect(() => {
    if (!open) return;
    const step = SCRIPT[stepIndex];
    if (!step) return;
    if (step.ask && !messages.some((m) => m.text === step.ask)) {
      setMessages((m) => [...m, { from: "dox", text: step.ask, quickReplies: step.quickReplies }]);
    } else if (step.from === "dox" && step.text) {
      const rendered = typeof step.text === "function" ? step.text(answers) : step.text;
      if (!messages.some((m) => m.text === rendered)) {
        setMessages((m) => [...m, { from: "dox", text: rendered }]);
      }
      if (step.cta === "ready") setShowCTA(true);
      // Handoff / any 'dox'-authored step marks the end of the scripted flow —
      // advance the state machine so the free-chat composer renders and the
      // visitor can now ask Claude anything.
      if (stepIndex < SCRIPT.length) {
        setStepIndex(SCRIPT.length);
      }
    }
  }, [open, stepIndex, answers, messages]);

  const handleAnswer = useCallback(
    (rawText) => {
      const text = String(rawText || "").trim();
      if (!text) return;
      setMessages((m) => [...m, { from: "user", text }]);

      const step = SCRIPT[stepIndex];
      // Scripted phase: capture answer, advance.
      if (step?.field) {
        setAnswers((a) => ({ ...a, [step.field]: text }));
        setStepIndex((i) => i + 1);
        return;
      }

      // Preview-intent detection: if the visitor asks Dox to "show", "walk
      // through", "demo", "preview", or "how would you handle…", offer the
      // Dox Preview\u2122 experience directly. Only surfaced when the
      // customer is authenticated (preview writes real records).
      const isLoggedIn = !!localStorage.getItem("ezdox_token");
      const previewIntent =
        previewAvailable && isLoggedIn &&
        /(preview|show me|walk (me )?through|how (would|do) you|demo)/i.test(text);
      if (previewIntent) {
        setMessages((m) => [
          ...m,
          {
            from: "dox",
            text: "Happy to. Let me walk you through exactly how I'd handle this — no payment, no filing. Just so you can see me work.",
            action: "preview",
          },
        ]);
        return;
      }

      // Free-chat phase → call Claude.
      setSending(true);
      api
        .post("/dox/public-chat", { message: text, ...answers })
        .then((res) => {
          const answer = res?.data?.answer || "I'm here — ask me anything.";
          const verification = res?.data?.verification || null;
          setMessages((m) => [...m, { from: "dox", text: answer, verification }]);
          if (res?.data?.suggest_signup) setShowCTA(true);
        })
        .catch(() => {
          setMessages((m) => [
            ...m,
            {
              from: "dox",
              text: "I'm having a small hiccup. Want me to just get started for you? Free 14-day trial, no card.",
            },
          ]);
          setShowCTA(true);
        })
        .finally(() => setSending(false));
    },
    [stepIndex, answers, previewAvailable]
  );

  const submit = (e) => {
    e?.preventDefault();
    if (sending) return;
    const t = input.trim();
    if (!t) return;
    setInput("");
    handleAnswer(t);
  };

  // Compute the appropriate emotion for Dox based on where we are in the flow.
  // idle → resting/breathing; greeting → wave + spark; thinking → dots (while
  // Claude generates); done → sparkle (when the CTA card is visible).
  const doxEmotion = sending
    ? "thinking"
    : showCTA
    ? "done"
    : open
    ? "greeting"
    : "idle";

  return (
    <>
      {/* Floating launcher — Dox himself (not a generic chat bubble) */}
      <button
        data-testid="dox-widget-launcher"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close Dox" : "Chat with Dox"}
        className="dox-launcher-btn fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[70] flex items-center justify-center rounded-full transition-all hover:scale-[1.05] active:scale-95"
      >
        {open ? (
          <span className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-dox-orange shadow-[0_0_40px_rgba(30,120,255,0.55)] flex items-center justify-center border border-white/15">
            <X size={22} className="text-white" />
          </span>
        ) : (
          <span className="relative inline-flex items-center justify-center">
            {/* Soft brand-blue halo behind Dox — draws the eye without hiding him */}
            <span
              aria-hidden="true"
              className="absolute inset-0 -m-2 rounded-full bg-dox-orange/25 blur-2xl"
            />
            <span className="relative inline-flex items-center justify-center h-16 w-16 sm:h-[76px] sm:w-[76px] rounded-full bg-white shadow-[0_10px_30px_rgba(0,0,0,0.35),_0_0_36px_rgba(30,120,255,0.35)] ring-1 ring-black/10">
              <DoxMascot size={62} emotion={greeted ? "idle" : "greeting"} />
            </span>
            {!greeted && (
              <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-400 animate-pulse ring-2 ring-white" />
            )}
          </span>
        )}
      </button>

      {/* Auto-greet bubble */}
      <AnimatePresence>
        {showGreetBubble && !open && (
          <motion.button
            data-testid="dox-widget-greet-bubble"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8 }}
            className="fixed bottom-[110px] sm:bottom-[124px] right-5 sm:right-6 z-[69] max-w-[280px] text-left glass-strong border border-white/15 rounded-2xl rounded-br-sm px-4 py-3 shadow-2xl hover:border-dox-orange/40 transition-colors"
          >
            <div className="flex items-start gap-2.5">
              <DoxMascot size={30} emotion="greeting" />
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-dox-orange">Dox</div>
                <div className="text-sm text-white/90 leading-snug mt-0.5">
                  Hi, I&apos;m Dox — your AI Employee. Tap to say hello.
                </div>
              </div>
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="dox-widget-panel"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.33, 1, 0.68, 1] }}
            className="fixed bottom-[110px] sm:bottom-[124px] right-5 sm:right-6 z-[68] w-[calc(100vw-2.5rem)] sm:w-[380px] max-w-[420px] h-[560px] max-h-[calc(100vh-8rem)] rounded-3xl glass-strong border border-white/15 shadow-2xl shadow-dox-orange/10 overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-gradient-to-r from-dox-orange/10 to-dox-magenta/10">
              <div className="relative">
                <DoxMascot size={40} emotion={doxEmotion} />
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-[#0d1428]" />
              </div>
              <div className="min-w-0">
                <div className="font-display font-black text-white text-sm leading-tight">Dox</div>
                <div className="text-[11px] text-emerald-300 leading-tight">
                  {sending ? "Thinking…" : "Online · Your AI Compliance Employee"}
                </div>
              </div>
              <button
                data-testid="dox-widget-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="ml-auto h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center text-white/70 hover:text-white transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              role="log"
              aria-live="polite"
              aria-atomic="false"
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-thin"
            >
              {messages.map((m, i) => (
                <Bubble
                  key={i}
                  from={m.from}
                  text={m.text}
                  quickReplies={m.quickReplies}
                  action={m.action}
                  verification={m.verification}
                  onQuickReply={handleAnswer}
                  onPreview={() => { setOpen(false); openDoxPreview?.(); }}
                  isLatest={i === messages.length - 1}
                />
              ))}
              {sending && (
                <div className="flex items-end gap-2">
                  <DoxMascot size={26} emotion="thinking" />
                  <div className="glass rounded-2xl rounded-bl-sm px-3.5 py-2.5 flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse" style={{ animationDelay: "0.15s" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse" style={{ animationDelay: "0.3s" }} />
                  </div>
                </div>
              )}

              {/* Consultative CTA card — appears after handoff or when Dox suggests signup */}
              {showCTA && (
                <div
                  data-testid="dox-widget-cta-card"
                  className="mt-1 rounded-2xl border border-dox-orange/40 bg-gradient-to-br from-dox-orange/15 to-dox-magenta/10 p-4"
                >
                  <div className="flex items-center gap-2 text-dox-orange text-[11px] font-bold uppercase tracking-wider">
                    <Sparkles size={12} /> Ready when you are
                  </div>
                  <div className="mt-1.5 text-white font-display font-black text-base leading-tight">
                    Ready to let me handle your paperwork?
                  </div>
                  <button
                    onClick={() =>
                      openTrial({
                        prefill: {
                          company: answers.business_type ? `${answers.business_type} — ${answers.state || ""}`.trim() : "",
                        },
                      })
                    }
                    data-testid="dox-widget-trial-cta"
                    className="mt-3 w-full rounded-full bg-dox-orange hover:bg-dox-orangeHover text-white font-bold px-5 py-3 min-h-[46px] text-sm inline-flex items-center justify-center gap-2 shadow-lg shadow-dox-orange/25 transition-all hover:scale-[1.02] active:scale-95"
                  >
                    Start My 14-Day Free Trial <ArrowRight size={15} />
                  </button>
                  <div className="text-[10px] text-white/45 text-center mt-2">No credit card required</div>
                </div>
              )}
            </div>

            {/* Composer */}
            {stepIndex >= SCRIPT.length && (
              <form
                onSubmit={submit}
                className="flex items-center gap-2 px-3 py-3 border-t border-white/10 bg-black/20"
              >
                <input
                  data-testid="dox-widget-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask Dox anything…"
                  className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-white/35 outline-none focus:border-dox-orange focus:ring-2 focus:ring-dox-orange/20 transition"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || sending}
                  data-testid="dox-widget-send"
                  aria-label="Send"
                  className="h-10 w-10 rounded-full bg-dox-orange hover:bg-dox-orangeHover disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors shrink-0"
                >
                  <Send size={15} />
                </button>
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Bubble({ from, text, quickReplies, action, verification, onQuickReply, onPreview, isLatest }) {
  const isDox = from === "dox";
  return (
    <div className={`flex items-end gap-2 ${isDox ? "" : "flex-row-reverse"}`}>
      {isDox && <DoxMascot size={26} emotion="idle" />}
      <div className="max-w-[85%]">
        <div
          className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
            isDox
              ? "glass rounded-bl-sm text-white/95"
              : "bg-dox-orange text-white rounded-br-sm shadow-lg shadow-dox-orange/20"
          }`}
        >
          {text}
        </div>
        {isDox && verification && <DoxVerifiedLine verification={verification} compact />}
        {isDox && isLatest && action === "preview" && onPreview && (
          <div className="mt-2">
            <button
              data-testid="dox-widget-preview-cta"
              onClick={onPreview}
              className="inline-flex items-center gap-1.5 text-[12px] rounded-full border border-dox-orange/40 bg-dox-orange/10 hover:bg-dox-orange/25 text-dox-orange px-3.5 py-1.5 font-medium transition-colors"
            >
              <Play size={12} strokeWidth={2.5} /> {"Preview what I'll do"}
            </button>
          </div>
        )}
        {isDox && isLatest && quickReplies?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {quickReplies.map((q) => (
              <button
                key={q}
                data-testid={`dox-quick-${q.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => onQuickReply(q)}
                className="text-[12px] rounded-full border border-dox-orange/40 bg-dox-orange/10 hover:bg-dox-orange/25 text-dox-orange px-3 py-1.5 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
